/**
 * TransactionDrawer - Slide-out panel showing transactions for a clicked node/edge
 *
 * When clicking an expense node or an edge in the Sankey diagram,
 * this drawer opens to show the individual transactions that make up that flow.
 *
 * Per WP-FLOWS-1 Deliverable 10
 */

import { useEffect, useRef, useState } from 'react';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import TransactionList from '@/components/transactions/TransactionList';
import TransactionRow from '@/components/transactions/TransactionRow';
import { Spinner } from '@/components/ui';
import type { TransactionWithSplits } from '@/services/transactions';

interface TransactionDrawerProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  periodStart: string;
  periodEnd: string;
  transactions: TransactionWithSplits[];
  isLoading?: boolean;
  totalAmount?: number;
  transactionCount?: number;
  onClose: () => void;
  onTransactionClick?: (transaction: TransactionWithSplits) => void;
}

export default function TransactionDrawer({
  isOpen,
  title,
  subtitle,
  periodStart,
  periodEnd,
  transactions,
  isLoading = false,
  totalAmount,
  transactionCount,
  onClose,
  onTransactionClick,
}: TransactionDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [selectedIds] = useState<Set<string>>(new Set());

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus drawer when opened
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  // Format period for display
  const periodDisplay = `${formatDate(periodStart, { month: 'short', day: 'numeric' })} - ${formatDate(periodEnd, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // Calculate total if not provided
  const displayTotal = totalAmount ?? transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const displayCount = transactionCount ?? transactions.length;

  const handleTransactionClick = (transaction: TransactionWithSplits) => {
    if (onTransactionClick) {
      onTransactionClick(transaction);
    }
  };

  // No-op for checkbox selection (not used in drawer)
  const handleSelectTransaction = () => {};

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/30 z-40 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-50',
          'transform transition-transform duration-300 ease-in-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label={`Transactions for ${title}`}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{title}</h2>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">{periodDisplay}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Summary */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(displayTotal)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Transactions</p>
              <p className="text-xl font-semibold text-gray-900">{displayCount}</p>
            </div>
          </div>
        </div>

        {/* Transaction List */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Spinner size="lg" />
                <p className="mt-3 text-sm text-gray-500">Loading transactions...</p>
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6">
              <svg
                className="w-12 h-12 text-gray-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="text-gray-600 font-medium">No transactions found</p>
              <p className="text-sm text-gray-400 mt-1 text-center">
                There are no transactions for this category in the selected period
              </p>
            </div>
          ) : (
            <TransactionList
              transactions={transactions}
              selectedIds={selectedIds}
              onSelectTransaction={handleSelectTransaction}
              onClickTransaction={handleTransactionClick}
              showCheckboxes={false}
              className="h-full"
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {transactions.length > 0 ? `Showing ${transactions.length} transactions` : ''}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Simple transaction list for the drawer when virtualization is not needed
 */
export function SimpleTransactionList({
  transactions,
  onTransactionClick,
}: {
  transactions: TransactionWithSplits[];
  onTransactionClick?: (transaction: TransactionWithSplits) => void;
}) {
  const [selectedIds] = useState<Set<string>>(new Set());

  return (
    <div className="divide-y divide-gray-100">
      {transactions.map((transaction) => (
        <TransactionRow
          key={transaction.id}
          transaction={transaction}
          isSelected={selectedIds.has(transaction.id)}
          onSelect={() => {}}
          onClick={onTransactionClick}
          showCheckbox={false}
        />
      ))}
    </div>
  );
}
